import { Injectable, Injector, effect, inject, signal, untracked } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { firstValueFrom, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';
import { ChatPaymentsService } from 'src/shared/services/chat-payments/chat-payments.service';
import { bookingexternal, trialmanagementexternal } from 'src/shared/services/client/client';
import { TimezoneService } from 'src/shared/services/timezone/timezone.service';
import { UserStore } from '../user.store';
import { ChatStatus } from './chat-data.store';
import {
  CHAT_AVAILABILITY_STORE,
  CHAT_CONNECTION_STORE,
  CHAT_DATA_STORE,
  CHAT_UI_STORE,
} from './stream-chat-tokens/stream-chat.tokens';

@Injectable()
export class ChatFacade {
  readonly ui = inject(CHAT_UI_STORE);
  readonly data = inject(CHAT_DATA_STORE);
  readonly conn = inject(CHAT_CONNECTION_STORE);
  readonly availability = inject(CHAT_AVAILABILITY_STORE);
  readonly injector = inject(Injector);

  private timezone = inject(TimezoneService);
  private userStore = inject(UserStore);
  private user = this.userStore.user;
  private pets = this.userStore.pets;
  private chatPayments = inject(ChatPaymentsService);
  private isConnecting = signal(false);

  constructor() {
    effect(() => {
      if (this.user()) {
        untracked(async () => {
          await firstValueFrom(this.connectUser());
        });
      }
    });
  }

  connectUser() {
    if (this.isConnecting()) {
      return throwError(() => new Error('Connecting to a chat is already in progress, please wait for it to complete'));
    }
    this.isConnecting.set(true);
    return this.data.loadMyChats().pipe(
      switchMap(({ latestIncompleteChat, latestChat }) => {
        if (!latestChat && !latestIncompleteChat) {
          return of(undefined);
        }
        const requests = [this.connectToChat(latestIncompleteChat || (latestChat as bookingexternal.GetChatResponse))];
        return forkJoin(requests);
      }),
      tapResponse({
        next: () => {
          this.isConnecting.set(false);
        },
        error: (error) => {
          console.error('Error connecting to a chat:', error);
          this.isConnecting.set(false);
        },
        complete: () => {
          this.isConnecting.set(false);
        },
      })
    );
  }
  createNewChatWithTrial(
    trial_doc_id: string,
    first_message: string,
    params: trialmanagementexternal.CreateTrialChatRequest
  ) {
    return this.data.startNewChatWithTrial(trial_doc_id, params).pipe(
      switchMap((response) => this.connectToNewChat(response.channel_id, first_message).pipe(map(() => response))),
      switchMap((chat) => this.data.loadMyChats().pipe(map(() => chat)))
    );
  }

  createNewChat({
    pet_doc_id,
    status,
    first_message,
    session_doc_id,
  }: {
    pet_doc_id: string;
    status?: ChatStatus;
    first_message?: string;
    session_doc_id?: string;
  }) {
    const params = {
      pet_doc_id,
      status: status || ChatStatus.curious,
      timezone: this.timezone.getClientTimezone(),
      chat_service_id: this.data.chatServiceDocId() || '',
      sc_session_doc_id: session_doc_id || undefined,
    };

    const checkPaymentStatus$ = of(params).pipe(
      switchMap((params) => {
        // Joii Tenants go through a payment flow before creating a chat.
        if (this.conn.tenant()?.friendly_id.includes('joii')) {
          return this.chatPayments
            .openPaymentDialog(params.chat_service_id, this.pets().find((p) => p.doc_id === pet_doc_id)?.name || '')
            .pipe(switchMap((confirmation) => (confirmation === ChatStatus.curious ? of(params) : of(undefined))));
        }
        return of(params);
      })
    );

    return checkPaymentStatus$.pipe(
      switchMap((params) => {
        if (!params) {
          // User cancelled the payment dialog, we do not create a new chat.
          return of(undefined);
        }
        return this.data.startNewChat(params).pipe(
          switchMap((response: bookingexternal.CreateChatResponse) =>
            this.connectToNewChat(response.channel_id, first_message).pipe(map(() => response))
          ),
          switchMap((chat) => this.data.loadMyChats().pipe(map(() => chat))),
          switchMap((chat) => {
            const fullChat = this.data.chats().find((c) => c.doc_id === chat.doc_id);
            // If the chat requires payment, redirect to the payment page.
            if (
              fullChat &&
              fullChat.status === ChatStatus.paymentRequired &&
              fullChat.payment_link &&
              fullChat.payment_status === 'Unpaid'
            ) {
              this.chatPayments.redirectToChatPayment(fullChat.payment_link);
            }
            return of(chat);
          }),
          tapResponse({
            next: () => {
              this.data.resetCreating();
            },
            error: (error) => {
              console.error('Error creating a chat:', error);
              this.data.resetCreating();
            },
            complete: () => {
              this.data.resetCreating();
            },
          })
        );
      })
    );
  }

  connectToNewChat(channel_id: string, first_message?: string) {
    const user = this.user();
    if (!user) {
      throw new Error('User is not set');
    }
    return this.conn
      .connectUser({
        doc_id: user.doc_id,
        name: `${user.first_name} ${user?.last_name}`,
      })
      .pipe(
        switchMap(() => this.conn.setChannels(user)),
        switchMap(() => {
          const channel = this.conn.getChannel(channel_id);
          if (channel) {
            this.conn.setActiveChannel(channel);
            this.conn.setActiveChannelListener(this.injector);
            return of(undefined);
          }
          return this.conn.watchAndSetActiveChannel(channel_id).pipe(
            tap(() => this.conn.setActiveChannelListener(this.injector)),
            map(() => undefined)
          );
        }),
        switchMap(() => (first_message ? this.conn.sendMessageToActiveChannel(first_message) : of(undefined)))
      );
  }

  connectToChat(chat: bookingexternal.GetChatResponse) {
    const user = this.user();
    if (!user) {
      throw new Error('User is not set');
    }
    return this.conn
      .connectUser({
        doc_id: user.doc_id,
        name: `${user.first_name} ${user?.last_name}`,
      })
      .pipe(
        switchMap(() => this.conn.setChannels(user)),
        switchMap(() => {
          if (this.conn.getChannel(chat.channel_id)) {
            return of(undefined);
          }
          return this.conn.watchAndSetActiveChannel(chat.channel_id).pipe(map(() => undefined));
        }),
        tapResponse({
          next: () => {
            this.conn.setActiveChannel(this.conn.getChannel(chat.channel_id));
            this.conn.updateUnreadCount();
            this.conn.updateLatestMessage();
            this.ui.openWidget();
          },
          error: (error) => {
            console.error('Error setting channels:', error);
          },
        })
      );
  }

  openStripePaymentLink(chat: bookingexternal.GetChatResponse | undefined) {
    if (chat && chat.payment_link && chat.payment_status === 'Unpaid') {
      this.chatPayments.redirectToChatPayment(chat.payment_link);
    } else {
      console.warn('No payment link available or payment already completed.');
    }
  }

  reset() {
    this.ui.resetUI();
    this.conn.disconnect();
  }

  resetAndConnectUser() {
    this.data.resetData();
    this.reset();
    this.connectUser();
  }

  setPendingChatStatus(status: ChatStatus | null) {
    this.ui.setPendingChatStatus(status);
  }

  consumePendingChatStatus(): ChatStatus | null {
    return this.ui.consumePendingChatStatus();
  }
}
